import { fetchPages, forEachPageBatched, fetchAllCount } from '../db/pages';
import { fetchTagNames } from '../db/tags';
import { cosineSimilarity } from '../store/pageSimilarity';
import { aggregateTags } from '../main/tagSelector/tag_utils';
import type { AggregateTagAttachment } from '../main/tagSelector/tag_utils';
import { reportProgress, clearProgress } from '../db/progress';
import { logger } from '../utils/logger';
import { SystemTags } from '../db/constants';
import type { Page } from '../db/pages';

// Each compute request gets a new runId. The abort message increments runId,
// invalidating any in-flight computation without needing a shared mutable flag.
let currentRunId = 0;

self.addEventListener('message', async (event: Event) => {
  const { type, ...payload } = (event as MessageEvent).data;

  if (type === 'abort') {
    currentRunId++;
    return;
  }

  if (type === 'compute') {
    currentRunId++;
    const myRunId = currentRunId;
    await runComputation(payload, () => myRunId !== currentRunId);
  }
});

// Common English stop words and short words that make poor tag signals.
const TITLE_STOP_WORDS = new Set([
  'the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'was',
  'has', 'how', 'now', 'see', 'two', 'way', 'who', 'its', 'let', 'say',
  'too', 'use', 'get', 'got', 'did', 'had', 'one', 'out', 'via',
  'with', 'from', 'that', 'this', 'have', 'they', 'will', 'been', 'into',
  'more', 'than', 'when', 'what', 'your', 'some', 'time', 'also', 'just',
  'does', 'about', 'which', 'there', 'their', 'would', 'could', 'should',
  'other', 'then', 'these', 'those', 'were', 'here', 'like', 'make',
  'made', 'over', 'such', 'even', 'most', 'many', 'much', 'very', 'well',
  'back', 'good', 'only', 'both', 'each', 'same', 'take', 'give', 'come',
  'after', 'before', 'through', 'between', 'under', 'while', 'where',
  'being', 'having', 'using', 'used', 'based', 'part', 'every',
]);

/**
 * Suggest tags from existing tag names that match words in the selected pages' titles.
 * Both tokenized (individual words) and non-tokenized (full title substring) matches
 * are considered. Matches are given high proportion scores so they rank at the top of
 * suggested tags even when embedding similarity hasn't run yet.
 */
function titleTagMatches(
  selectedPages: Page[],
  allTagNames: string[],
): { [key: string]: AggregateTagAttachment } {
  const result: { [key: string]: AggregateTagAttachment } = {};

  const titleWords = new Set<string>();
  const titleTexts: string[] = [];

  for (const page of selectedPages) {
    if (!page.title) continue;
    const titleLower = page.title.toLowerCase();
    titleTexts.push(titleLower);
    for (const word of titleLower.split(/\W+/)) {
      if (word.length >= 3 && !TITLE_STOP_WORDS.has(word)) {
        titleWords.add(word);
      }
    }
  }

  if (titleWords.size === 0 && titleTexts.length === 0) return result;

  for (const tagName of allTagNames) {
    if (SystemTags.includes(tagName)) continue;
    const tagLower = tagName.toLowerCase();
    let score = 0;

    // Exact word match: tag text equals one of the tokenized title words
    if (titleWords.has(tagLower)) {
      score = 1.0;
    }

    // Tag text appears as a substring of the full title (catches multi-word tags)
    if (score < 0.95 && tagLower.length >= 3) {
      for (const titleText of titleTexts) {
        if (titleText.includes(tagLower)) {
          score = 0.95;
          break;
        }
      }
    }

    // A title word appears inside the tag (e.g. tag "react-hooks", word "react")
    if (score < 0.85) {
      for (const word of titleWords) {
        if (word.length >= 4 && tagLower.includes(word)) {
          score = 0.85;
          break;
        }
      }
    }

    if (score > 0) {
      result[tagName] = {
        name: tagName,
        humanEditProportion: 1.0,
        inferenceStrength: score,
        proportion: score,
      };
    }
  }

  return result;
}

async function runComputation(
  {
    selectedUrls,
    taskId,
    source,
  }: {
    selectedUrls: string[];
    taskId: string;
    source: string;
  },
  isAborted: () => boolean
) {
  const similarUrls: { [key: string]: number } = {};
  const breakdowns: { [url: string]: { content: number; opener: number; access: number } } = {};
  const startTime = performance.now();

  try {
    const selectedPages = await fetchPages(selectedUrls);
    if (isAborted() || selectedPages.length === 0) return;

    const totalPages = await fetchAllCount();
    if (isAborted()) return;

    const total = totalPages * selectedPages.length;
    let count = 0;
    await reportProgress(taskId, source, 'similarity', count, total);

    logger.info(`[SimilarityWorker] Starting: ${selectedPages.length} selected × ${totalPages} total`);

    try {
      await forEachPageBatched(500, async (batchPages) => {
        if (isAborted()) throw new DOMException('Aborted', 'AbortError');

        for (const targetPage of batchPages) {
          const targetEmbedding = targetPage.embedding;
          const targetUrl = targetPage.nurl.normalized;

          for (const selectedPage of selectedPages) {
            count++;
            if (count % 500 === 0) {
              if (isAborted()) throw new DOMException('Aborted', 'AbortError');
              await reportProgress(taskId, source, 'similarity', count, total);
            }

            if (targetEmbedding && selectedPage.embedding) {
              const contentSimilarity = Math.max(
                0,
                cosineSimilarity(targetEmbedding, selectedPage.embedding)
              );
              const accessSimilarity = (selectedPage.timeAdjacentUrls || []).includes(targetUrl) ? 1 : 0;
              const openerSimilarity =
                (selectedPage.openedUrls || []).includes(targetUrl) ||
                (selectedPage.openerUrls || []).includes(targetUrl)
                  ? 1
                  : 0;
              const similarity =
                0.7 * contentSimilarity + 0.15 * openerSimilarity + 0.15 * accessSimilarity;

              if (similarity > 0) {
                similarUrls[targetUrl] =
                  (similarUrls[targetUrl] || 0) + similarity / selectedPages.length;
                if (!breakdowns[targetUrl])
                  breakdowns[targetUrl] = { content: 0, opener: 0, access: 0 };
                breakdowns[targetUrl].content += contentSimilarity / selectedPages.length;
                breakdowns[targetUrl].opener += openerSimilarity / selectedPages.length;
                breakdowns[targetUrl].access += accessSimilarity / selectedPages.length;
              }
            }
          }
        }
      });
    } catch (e: any) {
      if (e.name === 'AbortError') {
        logger.debug('[SimilarityWorker] Aborted mid-batch.');
        return;
      }
      throw e;
    }

    if (isAborted()) return;

    // Log top matches for debugging
    const topMatches = Object.entries(similarUrls)
      .filter(([url]) => !selectedUrls.includes(url))
      .sort(([, a], [, b]) => b - a)
      .slice(0, 25);
    if (topMatches.length > 0) {
      const matchPages = await fetchPages(topMatches.map(([url]) => url));
      logger.debug('[SimilarityWorker] Top results:');
      topMatches.forEach(([url, score], i) => {
        const page = matchPages.find((p) => p.nurl.normalized === url);
        const b = breakdowns[url] || { content: 0, opener: 0, access: 0 };
        logger.debug(
          `${(i + 1).toString().padStart(2, ' ')}. [${score.toFixed(3)}] ` +
            `(C:${b.content.toFixed(2)} O:${b.opener.toFixed(2)} A:${b.access.toFixed(2)}) ` +
            `${page?.title || 'Unknown'} (${url})`
        );
      });
    }

    const nonSelectedSimilarUrls = Object.fromEntries(
      Object.entries(similarUrls).filter(([url]) => !selectedUrls.includes(url))
    );
    const nonSelectedScores = Object.values(nonSelectedSimilarUrls);
    const topScore = nonSelectedScores.length > 0 ? Math.max(...nonSelectedScores) : 0;
    const similarityFloor = Math.max(topScore * 0.6, 0.2);
    const verySimilarUrls = Object.fromEntries(
      Object.entries(nonSelectedSimilarUrls).filter(([, s]) => s >= similarityFloor)
    );
    const verySimilarPages = await fetchPages(Object.keys(verySimilarUrls));
    const similarTags = aggregateTags(verySimilarPages);

    // Augment with title-word matches: existing tags whose text appears in the
    // selected pages' titles receive a high proportion so they surface first.
    const allTagNames = await fetchTagNames() as string[];
    const titleMatches = titleTagMatches(selectedPages, allTagNames);
    for (const [tagName, tagData] of Object.entries(titleMatches)) {
      if (!similarTags[tagName] || similarTags[tagName].proportion < tagData.proportion) {
        similarTags[tagName] = tagData;
      }
    }

    logger.info(
      `[SimilarityWorker] Done in ${(performance.now() - startTime).toFixed(0)}ms. ` +
        `${Object.keys(verySimilarUrls).length} similar pages found (floor: ${similarityFloor.toFixed(3)}, top: ${topScore.toFixed(3)}).`
    );
    const topTags = Object.values(similarTags)
      .sort((a, b) => b.proportion - a.proportion)
      .slice(0, 10);
    if (topTags.length > 0) {
      logger.debug('[SimilarityWorker] Top similar tags:');
      topTags.forEach((tag, i) => {
        logger.debug(
          `${(i + 1).toString().padStart(2, ' ')}. [${tag.proportion.toFixed(3)}] ${tag.name}`
        );
      });
    } else {
      logger.debug('[SimilarityWorker] No similar tags found.');
    }

    (self as any).postMessage({ type: 'result', similarUrls, similarTags });
  } catch (e: any) {
    logger.error('[SimilarityWorker] Fatal error:', e);
  } finally {
    await clearProgress(taskId, !isAborted());
  }
}
